import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../theming/useTheme';
import { VrLineHeight } from '../../../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { MandatoryAdditionalStyle } from '../../../components.utils';
import { AspectRatio } from '../image.types';
import { SemanticTagThemeProps, getStyleBySize } from '../../../../typography/semanticTags/semanticTags.utils';


const componentConfigThemeKey = 'imageWithFigcaptionWrapper';

export const getStyle = (aspectRatio: AspectRatio, vrLineHeight: VrLineHeight, figcaptionTextColor: string, backgroundColor: string) => StyleSheet.create({
  base: {
    backgroundColor,
    position: 'relative',
    overflow: 'hidden',
    margin: 0,
    boxSizing: 'border-box',
    flex: '1 1 100%',
    minWidth: '0%',
    paddingTop: `calc(100% / (${aspectRatio.x / aspectRatio.y}))`,
  },
  figcaption: {
    color: figcaptionTextColor,
    [MEDIA_BREAKPOINTS.LARGE]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
      marginLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
    },
  },
});

interface ImageWrapperDataProps {
  aspectRatio: AspectRatio
  figcaption: string
  dataId?: string
  children: React.ReactNode
}

type ImageWithFigcaptionWrapperProps = ImageWrapperDataProps & MandatoryAdditionalStyle;

interface ImageWithFigcaptionWrapperThemeProps extends SemanticTagThemeProps {
  vrLineHeight: VrLineHeight
  figcaptionTextColor: string
  backgroundColor: string
}

type ImageWithFigcaptionWrapperComponentProps = ImageWithFigcaptionWrapperProps & ImageWithFigcaptionWrapperThemeProps;

export const ImageWithFigcaptionWrapperComponent: React.FunctionComponent<ImageWithFigcaptionWrapperComponentProps> = ({ fontSizesStyles, additionalStyle, aspectRatio, figcaption, vrLineHeight, figcaptionTextColor, backgroundColor, dataId, children }) => {
  const imageWrapperStyle = getStyle(aspectRatio, vrLineHeight, figcaptionTextColor, backgroundColor);
  const figcaptionText = getStyleBySize(fontSizesStyles, TYPOGRAPHY_SIZE.NORMAL);
  return (
    <figure data-id={dataId} className={css(additionalStyle.style)}>
      <div className={css(imageWrapperStyle.base)}>
        {children}
      </div>
      <figcaption className={css(imageWrapperStyle.figcaption, figcaptionText)}>{figcaption}</figcaption>
    </figure>
  );
};

const mapThemeProps = (theme: ImageWithFigcaptionWrapperThemeProps) => {
  return {
    vrLineHeight: theme.vrLineHeight,
    figcaptionTextColor: theme.figcaptionTextColor,
    backgroundColor: theme.backgroundColor,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const ImageWithFigcaptionWrapper = (props: ImageWithFigcaptionWrapperProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ImageWithFigcaptionWrapperComponent {...themeContext} {...props} />;
};
